import React from "react";
import { TableColumnData } from "../../../types/common";
import { useTimeConverter } from "../../../hooks/hooks";
import { useTranslation } from "react-i18next";
import { TestResultWideDataObj } from "../../../types/dataSearch";
import { Gender } from "../../../types/common";

/**
 * 검사결과 "문항 원점수(가로)" 데이터셋 컬럼 정의(마스터).
 * 고정 프로필 컬럼 + columnKeys(문항 키, 예: "1-1") 만큼의 동적 컬럼.
 */
export function useTestResultWideColumns(columnKeys: string[]) {
  const { t } = useTranslation("dataSearch");
  const { timeConverter } = useTimeConverter();

  const genderText = (g: Gender) =>
    g === Gender.MALE ? t("columns.genderValue.male") : g === Gender.FEMALE ? t("columns.genderValue.female") : "-";

  const numText = (v: number | null | undefined) => (v === null || v === undefined ? "-" : String(v));

  const profileColumns: TableColumnData<TestResultWideDataObj>[] = [
    {
      accessor: "name",
      header: t("columns.name"),
      getNode: (r: TestResultWideDataObj) => <span>{r.name ?? "-"}</span>,
      sortable: false,
    },
    {
      accessor: "userCode",
      header: t("columns.userCode"),
      getNode: (r: TestResultWideDataObj) => <span>{r.userCode ?? "-"}</span>,
      sortable: false,
    },
    {
      accessor: "guardianName",
      header: t("columns.guardianName"),
      getNode: (r: TestResultWideDataObj) => <span>{r.guardianName ?? "-"}</span>,
      sortable: false,
    },
    {
      accessor: "guardianPhone",
      header: t("columns.guardianPhone"),
      getNode: (r: TestResultWideDataObj) => <span>{r.guardianPhone ?? "-"}</span>,
      sortable: false,
      width: 160,
    },
    {
      accessor: "birthday",
      header: t("columns.birthday"),
      getNode: (r: TestResultWideDataObj) => <span>{r.birthday ?? "-"}</span>,
      sortable: false,
    },
    {
      accessor: "testedAt",
      header: t("columns.testedAt"),
      getNode: (r: TestResultWideDataObj) => (
        <span>{r.testedAt ? timeConverter.convertToLocalTime(r.testedAt) : "-"}</span>
      ),
      sortable: true,
    },
    {
      accessor: "ageYear",
      header: t("columns.ageYear"),
      getNode: (r: TestResultWideDataObj) => <span>{numText(r.ageYear)}</span>,
      sortable: true,
    },
    {
      accessor: "ageBand",
      header: t("columns.ageBand"),
      getNode: (r: TestResultWideDataObj) => <span>{r.ageBand ?? "-"}</span>,
      sortable: false,
    },
    {
      accessor: "gender",
      header: t("columns.gender"),
      getNode: (r: TestResultWideDataObj) => <span>{genderText(r.gender)}</span>,
      sortable: false,
    },
    {
      accessor: "state",
      header: t("columns.state"),
      getNode: (r: TestResultWideDataObj) => <span>{r.state ?? "-"}</span>,
      sortable: false,
    },
    {
      accessor: "city",
      header: t("columns.city"),
      getNode: (r: TestResultWideDataObj) => <span>{r.city ?? "-"}</span>,
      sortable: false,
    },
  ];

  const scoreColumns: TableColumnData<TestResultWideDataObj>[] = columnKeys.map((key) => ({
    accessor: key as keyof TestResultWideDataObj,
    header: key,
    getNode: (r: TestResultWideDataObj) => <span>{r.scores?.[key] ?? ""}</span>,
    sortable: false,
  }));

  const columns: TableColumnData<TestResultWideDataObj>[] = [...profileColumns, ...scoreColumns];

  return { columns };
}
